import { css } from '@emotion/css';
import MarkdownRenderer, { Components } from 'react-markdown';
import gfm from 'remark-gfm';
import emoji from 'emoji-dictionary';
import { ReactNode } from 'react';
import { useTheme } from '@chakra-ui/react';
import { rgba } from 'polished';

import remarkComponentMarkers from '~/shared/utils/componentMarkers';
import { resolveLinkTarget } from '~/shared/utils/linkTarget';

// Real HTML targets, without the `auto` pseudo-value the embedder config accepts.
const EXPLICIT_TARGETS = ['_blank', '_self', '_top', '_parent'];

const tableWrapperStyles = css`
  margin: 16px 0;
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`;

const getMarkdownStyles = (primaryColor: string) => {
  return css`
    overflow-wrap: anywhere;

    & p:has(img) {
      margin: -16px -16px 0;
    }

    & img {
      width: 100%;
      max-width: 100%;
      border-radius: 8px 8px 0 0;
    }

    & ol li,
    & ul li {
      list-style-position: inside;
    }

    & h2 {
      font-size: 16px;
    }

    & h4 {
      font-size: 12px;
    }

    & button {
      font-size: 13px;
    }

    & pre {
      margin: 16px 0;
      overflow-x: auto;
      max-width: 100%;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 12px;
    }

    & pre code {
      white-space: pre-wrap;
      overflow-wrap: anywhere;
    }

    & :not(pre) > code {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      padding: 1px 4px;
      font-size: 0.9em;
      -webkit-box-decoration-break: clone;
      box-decoration-break: clone;
    }

    & a {
      font-style: italic;
      text-decoration: underline;
      color: inherit;
    }

    /* Table Styles - Matches theme.ts chat action button colors */
    & table {
      width: 100% !important;
      border-collapse: collapse !important;
      font-size: 10px !important;
    }

    & thead {
      color: ${primaryColor} !important;
      background-color: white !important;
    }

    & thead th {
      padding: 10px 16px !important;
      text-align: left !important;
      font-weight: 600 !important;
      font-size: 10px !important;
      letter-spacing: 0.02em !important;
      border-bottom: 1px solid #e2e8f0 !important;
    }

    & tbody tr {
      background-color: #ffffff !important;
      color: #828282 !important;
      border-bottom: 1px solid #e2e8f0 !important;
      transition: background-color 0.15s ease !important;
    }

    /* Row hover - uses primary color with 11% opacity */
    & tbody tr:hover {
      background-color: ${rgba(primaryColor, 0.11)} !important;
    }

    & tbody td {
      padding: 10px 16px !important;
    }

    & th,
    & td {
      vertical-align: top !important;
      overflow-wrap: break-word;
    }
  `;
};

export interface MarkdownProps {
  content: string;
  spanCustomComponents?: ReactNode[];
  components?: Components;
}

export default function Markdown({ content, components }: MarkdownProps) {
  const theme = useTheme();
  const primaryColor = theme.colors.main;
  const markdownStyles = getMarkdownStyles(primaryColor);

  const fixedMarkdown = content.trim().replaceAll(/\n\n/g, '\n\n&nbsp;\n\n');

  const emojiSupport = (text: { value: string }) =>
    text.value.replace(/:\w+:/gi, (name: string) => emoji.getUnicode(name));

  return (
    // No `rehype-raw` here on purpose: it hands message text to parse5, which
    // throws on malformed markup and takes the whole widget down (#2730).
    // Messages carry user-pasted and Salesforce-sourced content, so raw HTML is
    // untrusted anyway — react-markdown's default escapes it and shows it as text.
    // Components that need to be embedded in the text (citations, citation
    // buttons) therefore go through `remarkComponentMarkers` instead of being
    // round-tripped as HTML strings (#2735).
    <MarkdownRenderer
      className={`${markdownStyles} worknet-markdown`}
      remarkPlugins={[gfm, remarkComponentMarkers]}
      components={{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        text: emojiSupport as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        a: ({ href, children, target: targetAttr, node: _node, ...props }: any) => {
          // An explicit target on the node wins; otherwise the embedder's
          // `linkTarget` setting decides.
          const { target, rel } = EXPLICIT_TARGETS.includes(targetAttr)
            ? {
                target: targetAttr,
                rel: targetAttr === '_blank' ? 'noopener noreferrer' : undefined,
              }
            : resolveLinkTarget(href);

          return (
            <a href={href} target={target} rel={rel} {...props}>
              {children}
            </a>
          );
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        table: ({ node: _node, ...props }: any) => (
          <div className={tableWrapperStyles}>
            <table {...props} />
          </div>
        ),
        ...components,
      }}
    >
      {fixedMarkdown}
    </MarkdownRenderer>
  );
}
